import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { serviceApi } from '../../services/api'

interface Actor {
  name: string
  avatarUrl: string | null
  deptName: string | null
  position: string | null
}

interface Interaction {
  at: string
  source: string
  convRef: string | null
  sentiment: number
  intensity: number
  snippet: string | null
  reaction: string | null
  fromName: string
  toName: string
}

interface ActorDetail {
  actor: Actor
  interactions: Interaction[]
}

interface Props {
  actor: { ref: string; name: string }
  kind: 'employee' | 'client' | 'partner'
  onClose: () => void
}

function shortTime(at: string): string {
  const then = new Date(at).getTime()
  const diff = Date.now() - then
  const m = Math.round(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 30) return `${d}d ago`
  return new Date(at).toLocaleDateString()
}

// OSPROD-233: drill-in drawer for a single Service actor (an employee/client row
// in a leaderboard, or a most-criticized/praised entry). Clicking such a signal
// used to navigate away to /users/:id or the CRM section; now it opens this
// drawer showing the actor's recent interactions so you can see what happened.
export default function ActorInteractionsDrawer({ actor, kind, onClose }: Props) {
  const [detail, setDetail] = useState<ActorDetail | null>(null)

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [onClose])

  useEffect(() => {
    setDetail(null)
    serviceApi
      .interactionStressActor(`ref=${encodeURIComponent(actor.ref)}&window=30&kind=${kind}`)
      .then((d: ActorDetail) => setDetail(d))
      .catch(() => setDetail({ actor: { name: actor.name, avatarUrl: null, deptName: null, position: null }, interactions: [] }))
  }, [actor.ref, actor.name, kind])

  return (
    <div className="fixed inset-0 z-[150]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="absolute top-0 right-0 h-full w-[420px] max-w-[92vw] bg-card border-l border-border shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 flex items-start justify-between border-b border-border shrink-0">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{detail?.actor.name ?? actor.name}</p>
            <p className="text-xs text-muted-foreground">recent interactions · 30 days</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition duration-150 shrink-0 ml-2"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
          {detail === null && <p className="py-2 text-sm text-muted-foreground">Loading…</p>}
          {detail !== null && detail.interactions.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">No interactions found in range.</p>
          )}
          {detail !== null &&
            detail.interactions.map((it, i) => {
              const tone =
                it.sentiment < -0.05
                  ? { text: 'text-destructive', bg: 'bg-destructive/15' }
                  : it.sentiment > 0.05
                    ? { text: 'text-emerald-500', bg: 'bg-emerald-500/15' }
                    : { text: 'text-amber-500', bg: 'bg-amber-500/15' }
              return (
                <div key={i} className="rounded-md border border-border bg-muted/30 px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${tone.text} ${tone.bg}`}>
                      {it.sentiment.toFixed(2)}
                    </span>
                    <span className="text-xs text-foreground truncate flex-1 min-w-0">
                      {it.fromName} → {it.toName}
                    </span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">{it.source}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{shortTime(it.at)}</span>
                  </div>
                  {it.snippet && <p className="text-sm text-foreground/90 break-words">{it.snippet}</p>}
                  {it.reaction && <p className="text-xs italic text-muted-foreground mt-1">{it.reaction}</p>}
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
